import React, { useState, useEffect, useCallback } from 'react';
import { LabelValue } from 'src/library/types';
import { Select, Input, DatePicker, Tabs, Row, Table, Badge, Tooltip, Button, Modal, Menu, Dropdown } from 'antd';
import Navbar from 'src/common/Navbar/Navbar';
import lodash from 'lodash';
import {
  VehicleRequestLabelMap,
  tripStatusMap,
  VehicleRequestRecord,
  fetchHubsDetails,
  VehicleRequestProps,
  VehicleRequestISTState,
  rangeFilterOptionsIST,
} from 'src/components/pages/VehicleRequests/VehicleRequests.utils';
import moment from 'moment';
import { EditOutlined, ReloadOutlined, EllipsisOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import {
  getVehicleRequestCountIST,
  getVehicleRequestIST,
} from 'src/components/pages/VehicleRequests/VehicleRequest.actionsCreator';
import { formatDateTime } from 'src/utils/utils';
import { useDebounce } from 'src/library/hooks/useDebounce';
import Loader from 'src/common/Loader';
import { VehicleRequestStyles } from './VehicleRequest.styles';
import SimplePaginator from 'src/common/SimplePaginator';
import VendorCodeFilter from 'src/components/vendorcodefilter/VendorCodeFilter';
import AssignVehicleModal from './AssignVehicleModal';
import RejectVehicleRequestModal from './RejectVehicleRequestModal';
import {
  SET_VEHICLE_REQUEST_FILTERS,
  SET_VEHICLE_REQUEST_PAGINATION,
} from 'src/common/actions/constants';
import { useGenericState } from 'src/library/UseGenericState';
import { Link, useHistory } from 'react-router-dom';
import ResetPassword from 'src/auth/ResetPassword';
import { vehicleRequestsRoute } from 'src/routing/routeConstants';

import RouteDetailsModal from './RouteDetailsModal';

const { TabPane } = Tabs;

const VehicleRequest: React.FC<VehicleRequestProps> = (props: VehicleRequestProps) => {
  const {
    classes,
    bucketList = {},
    filterList,
    vehicleRequest,
    getVehicleRequest,
    getVehicleRequestCount,
    setFilters,
    setPagination,
    vendorConfig,
    t,
  } = props;

  const queryParameters = new URLSearchParams(window.location.search);
  const showPasswordResetModal = queryParameters.get('resetPassword');
  const history = useHistory();

  const { filters, pagination } = vehicleRequest;
  const { timeFilters } = filters;

  const [state, setState] = useGenericState<VehicleRequestISTState>({
    activeTabKey: 'vehicle_requests_all',
    selectedRowKeys: [],
    searchFilterQuery: '',
    searching: false,
    searchFilterId: '',
    isAssignVehicleModalVisible: false,
    isRejectVehicleRequestModalVisible: false,
    editVehicleData: {},
    searchTripNumberQuery: '',
    timeFilterColumnName: 'created_at',
    routeInfoDetails: null,
    selectedVendors: [],
  });

  const {
    activeTabKey,
    selectedRowKeys,
    searching,
    searchFilterId,
    isAssignVehicleModalVisible,
    isRejectVehicleRequestModalVisible,
    editVehicleData,
    searchTripNumberQuery,
    timeFilterColumnName,
    routeInfoDetails,
    selectedVendors,
  } = state;

  const indentSettings = lodash.get(vendorConfig, 'indent_settings', {});
  const enableIndentAcceptance = indentSettings.enable_indent_acceptance || false;

  const [searchFilterOptions, setSearchFilterOptions] = useState({});

  const handleSearch = useCallback(
    async (queryStr: string) => {
      setState({ searching: true });
      const hubs = await fetchHubsDetails({ queryStr }, searchFilterId);
      const filterDataOptions: LabelValue[] = [];
      hubs?.forEach((item) => {
        filterDataOptions.push({
          label: searchFilterId === 'planningHubIdList' ? item.code : item.name,
          value: item.id,
        });
      });
      setSearchFilterOptions((filterData) => ({
        ...filterData,
        [searchFilterId]: filterDataOptions,
      }));
      setState({ searching: false });
    },
    [searchFilterId],
  );

  const debouncedSearchFilter = useDebounce(state.searchFilterQuery, 300);
  const debouncedSearchTripNumber = useDebounce(searchTripNumberQuery, 300);

  useEffect(() => {
    if (debouncedSearchFilter) {
      handleSearch(debouncedSearchFilter);
    }
  }, [debouncedSearchFilter, handleSearch]);

  useEffect(() => {
    setFilters({
      ...vehicleRequest.filters,
      tripReferenceNumberList: debouncedSearchTripNumber ? [debouncedSearchTripNumber] : undefined,
    });
  }, [debouncedSearchTripNumber]);

  const fetchViewDetailsAndCount = () => {
    getVehicleRequest({ bucket: activeTabKey, ...vehicleRequest.filters, ...pagination });
    getVehicleRequestCount({
      bucketIds: Object.keys(bucketList),
      ...vehicleRequest.filters,
      ...pagination,
    });
  };

  const renderTimeFilter = (): React.ReactElement => {
    return (
      <div
        style={{
          marginLeft: '48px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Select
          onSelect={(val) => {
            setState({ timeFilterColumnName: val });
            if (vehicleRequest?.filters?.timeFilters?.[0]?.fromDateString) {
              setFilters({
                ...vehicleRequest.filters,
                timeFilters: [
                  {
                    ...(timeFilters?.[0] ?? {}),
                    columnName: val,
                  },
                ],
              });
            }
          }}
          defaultValue="created_at"
          className={classes.select}
          style={{ margin: 0 }}
        >
          {rangeFilterOptionsIST.map((option) => (
            <Select.Option key={option.key} value={option.key}>
              {t(option.value)}
            </Select.Option>
          ))}
        </Select>
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          className={classes.datePicker}
          onChange={(val) => {
            setFilters({
              ...vehicleRequest.filters,
              timeFilters: val?.length
                ? [
                    {
                      columnName: timeFilterColumnName,
                      fromDateString: moment(val[0])?.format('YYYY-MM-DD'),
                      toDateString: moment(val[1])?.format('YYYY-MM-DD'),
                    },
                  ]
                : undefined,
            });
          }}
          ranges={{
            [t('Today')]: [moment(), moment()],
            [t('Last 7 days')]: [moment().subtract(6, 'day'), moment()],
            [t('Last 30 days')]: [moment().subtract(29, 'day'), moment()],
            [t('This Month')]: [moment().startOf('month'), moment().endOf('month')],
          }}
          defaultValue={[moment().subtract(29, 'days'), moment()]}
        />
      </div>
    );
  };

  const onFilterSelect = (selectedFilter, id) => {
    setFilters({
      ...vehicleRequest.filters,
      [id]: selectedFilter,
    });
  };

  const handleVendorFilterChange = (filterData) => {
    const vendors = Array.isArray(filterData) ? filterData : [];
    const vendorIds = vendors.map((item) => item.value as string);
    setState({ selectedVendors: vendors });
    setFilters({
      ...vehicleRequest.filters,
      vendorIdsToFilter: vendorIds,
    });
  };

  useEffect(() => {
    fetchViewDetailsAndCount();
  }, [vehicleRequest.filters, pagination]);

  const handleSearchFilter = (searchFilterQuery, id) => {
    if (id !== searchFilterId) {
      setState({ searchFilterId: id });
    }
    setState({ searchFilterQuery });
  };

  const renderSearchTripNumber = (): React.ReactElement => {
    return (
      <Input.Search
        style={{ width: '250px' }}
        placeholder={t("Search Trip Number")}
        onChange={(e) => {
          setState({ searchTripNumberQuery: e.target.value });
        }}
      />
    );
  };

  const dropdownRender = (menu) => {
    if (searching) {
      return (
        <div style={{ padding: '24px' }}>
          <Loader />
        </div>
      );
    }
    return menu;
  };

  const renderFilters = () => {
    return (
      <Row style={{ background: '#fff', padding: '12px 16px', justifyContent: 'space-between' }}>
        <div>
          {filterList[activeTabKey]?.map(({ placeholder, type, id, mode, options }) => {
            const isInput = type === 'input';
            const isSelect = type === 'select';
            const isVendorFilter = id === 'vendorIdsToFilter';
            if (isVendorFilter) {
              return (
                <VendorCodeFilter
                  key={id}
                  value={selectedVendors}
                  onChange={handleVendorFilterChange}
                  placeholder={t(placeholder)}
                  mode={mode}
                  style={{ minWidth: 150, marginRight: 16 }}
                />
              );
            }
            return (
              <Select
                key={id}
                showArrow
                allowClear
                showSearch={!isSelect}
                mode={isInput || mode === 'multiple' ? 'tags' : undefined}
                size="small"
                maxTagCount={1}
                filterOption={() => true}
                placeholder={t(placeholder)}
                onChange={(filterData) => onFilterSelect(filterData, id)}
                value={vehicleRequest.filters[id]}
                dropdownRender={dropdownRender}
                style={{ minWidth: 150, marginRight: 16 }}
                onSearch={(str) => !isInput && !isSelect && handleSearchFilter(str, id)}
              >
                {isSelect
                  ? options.map(({ label, value }) => (
                    <Select.Option key={value} value={value}>
                      {t(label)}
                    </Select.Option>
                  ))
                  : (searchFilterOptions?.[id] ?? []).map(({ label, value }) => (
                    <Select.Option key={value} value={value}>
                      {t(label)}
                    </Select.Option>
                  ))}
              </Select>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SimplePaginator
            loading={vehicleRequest.loader}
            onFirstClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber,
                nextOrPrev: 'first',
              });
            }}
            onPrevClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber - 1,
                nextOrPrev: 'prev',
              });
            }}
            onNextClick={() => {
              setPagination({
                ...pagination,
                pageNumber: pagination.pageNumber + 1,
                nextOrPrev: 'next',
              });
            }}
            isNext={vehicleRequest.is_next_present}
            pageNumber={pagination.pageNumber}
          />
          <Select
            className={classes.selectWithoutBorder}
            onChange={(resultPerPage) => {
              setPagination({ ...pagination, resultPerPage });
            }}
            value={pagination.resultPerPage}
          >
            <Select.Option value={100}>{t('100/page')}</Select.Option>
            <Select.Option value={50}>{t('50/page')}</Select.Option>
            <Select.Option value={20}>{t('20/page')}</Select.Option>
            <Select.Option value={10}>{t('10/page')}</Select.Option>
          </Select>
          <Tooltip title={t("Refresh")}>
            <Button onClick={fetchViewDetailsAndCount} className={classes.refreshButton}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </Row>
    );
  };

  const handleAddEditWorker = (record) => {
    setState({ isAssignVehicleModalVisible: true });
    setState({ editVehicleData: record });
  };

  const handleRejectModal = (record) => {
    setState({ isRejectVehicleRequestModalVisible: true });
    setState({ editVehicleData: record });
  };

  const getISTDetailsRoute = (tripReferenceNumber) => {
    return `/ist/details/${tripReferenceNumber}`;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let filter = {
      lastSortedColumnValue: '',
      descendingOrder: false,
    };
    if (sorter.column) {
      filter = {
        lastSortedColumnValue: sorter.columnKey,
        descendingOrder: sorter.order === 'descend',
      };
    }
    setFilters({
      ...vehicleRequest.filters,
      ...filter,
    });
  };

  const returnMenu = (record, bucket) => {
    const hasMenuItems =
      (record.trip_status !== 'created');
    const menu = (
      <Menu>
        {record.trip_status !== 'created' && (
          <Menu.Item key="message">
            <Link
              to={`${getISTDetailsRoute(record['trip_reference_number'])}?activeTab=Comments`}
              target="_blank"
            >
              {t('Message')}
              {record.new_comment_exists && (
                <Badge color="#f50" dot style={{ marginLeft: '3px' }} />
              )}
            </Link>
          </Menu.Item>
        )}
      </Menu>
    );
    return { menu, hasMenuItems };
  };

  const generateColumnsToShow = useCallback((bucketWiseColumn, bucket) => {
    const columnData: {
      title: string;
      sorter: boolean;
      dataIndex: string;
      key: string;
      width: number;
    }[] = [];

    bucketWiseColumn?.forEach((item) => {
      const column_id = item.is_sortable ? item.sort_key : item.column_id;
      const itemToPush = {
        title: t(item.pretty_name),
        sorter: item.is_sortable,
        dataIndex: column_id,
        key: column_id,
        width: 150,
      };

      if (column_id === 'created_at' || column_id === 'last_status_change_time') {
        itemToPush['render'] = (x) => {
          if (x) return formatDateTime(x);
          return 'N/A';
        };
      }
      if (column_id === 'vehicle') {
        itemToPush['width'] = 250;
        itemToPush['render'] = (_, record) => {
          return record.worker_name ? (
            <>
              <p style={{ margin: 0 }}>
                {record.worker_name}
                {/* {['departed', 'completed'].indexOf(record.trip_status) === -1 ? (
                  <EditOutlined
                    onClick={() => handleAddEditWorker(record)}
                    style={{ marginLeft: '4px' }}
                  />
                ) : (
                  <></>
                )} */}
              </p>
              <p style={{ color: '#666666', fontSize: 12 }}>{record.vehicle_registration_number}</p>
            </>
          ) : (
            <div>
              <span
                role="button"
                style={{ color: '#0780FE', cursor: 'pointer' }}
                onClick={() => handleAddEditWorker(record)}
                tabIndex={record.id}
              >
                {enableIndentAcceptance && record.vendor_trip_status === 'accepted'
                  ? t('Complete Assignment') : `+ ${t('Assign')}`}
              </span>
              {(lodash.get(vendorConfig, 'indent_settings.allow_indent_rejection', false)) && (
                <>
                  <span> | </span>
                  <span
                    role="button"
                    style={{ color: '#ff0000', cursor: 'pointer' }}
                    onClick={() => handleRejectModal(record)}
                    tabIndex={record.id}
                  >
                    {t('Reject')}
                  </span>
                </>
              )}
            </div>
          );
        };
      }
      if (column_id === 'vehicle_make') {
        itemToPush['render'] = (_, record) => record.vehicle_make_code;
      }
      if (column_id === 'route_code') {
        itemToPush['render'] = (_, record) => {
          return (
            <div>
              <span>{record.route_code}</span>
              <InfoCircleOutlined
                style={{
                  marginLeft: '8px',
                  color: '#085492',
                }}
                onClick={() =>
                  setState({
                    routeInfoDetails: record.route_details,
                  })}
              />
            </div>
          );
        };
      }
      if (column_id === 'ist_reference_number') {
        itemToPush['render'] = (x, record) => {
          return (
            <Link
              to={getISTDetailsRoute(record[column_id])}
              target="_blank"
            >
              {record[column_id]}
            </Link>
          );
        };
      }
      if (column_id === 'status') {
        itemToPush['render'] = (_, record) => (
          <div>
            <Badge
              color={tripStatusMap[record.trip_status]?.color ?? '#87d068'}
              text={t(tripStatusMap[record.trip_status]?.label ?? 'NA')}
            />
          </div>
        );
      }
      if (column_id === 'actions') {
        itemToPush['render'] = (_, record) => {
          const { menu, hasMenuItems } = returnMenu(record, bucket);

          return hasMenuItems ? (
            <div style={{ fontSize: '18px' }}>
              <Dropdown overlay={menu} trigger={['click']}>
                <EllipsisOutlined />
              </Dropdown>
            </div>
          ) : null;
        };
      }
      if (column_id === 'deadline') {
        itemToPush['width'] = 200;
        itemToPush['render'] = (_, record) => {
          return record.deadline ? (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <ClockCircleOutlined style={{ marginRight: '8px' }} />
              {formatDateTime(record.deadline)}
            </span>
          ) : (
            <span>-</span>
          );
        };
      }
      columnData.push(itemToPush);
    });
    return columnData;
  }, []);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setState({ selectedRowKeys });
    },
  };

  const onBucketChange = (activeTabKey) => {
    setState({ activeTabKey });
    setPagination({ ...pagination, pageNumber: 1, nextOrPrev: 'first' });
    getVehicleRequest({ bucket: activeTabKey, ...vehicleRequest.filters, ...pagination });
  };

  const renderNavbar = () => (
    <Navbar
      title={t("Vehicle Requests MM")}
      leftComponents={renderTimeFilter}
      rightComponents={renderSearchTripNumber}
    />
  );

  const renderTabHeader = (key) => (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      {t(VehicleRequestLabelMap[key].label || key)}
      <Badge
        count={vehicleRequest.count[key] || 0}
        showZero
        style={{
          backgroundColor: VehicleRequestLabelMap[key].badgeColor ?? '#52c41a',
        }}
      />
    </div>
  );

  const renderViewTable = (key) => (
    <Table
      rowKey={(record: VehicleRequestRecord) => record['id']}
      className={classes.table}
      dataSource={vehicleRequest.page_data || []}
      columns={generateColumnsToShow(bucketList[key], key)}
      rowSelection={rowSelection}
      loading={vehicleRequest.loader}
      pagination={false}
      onChange={handleTableChange}
      scroll={{ x: 'max-content' }}
      rowClassName={(record) => {
        const enableIndentAcceptance = lodash.get(vendorConfig, 'indent_settings.enable_indent_acceptance', false);
        return enableIndentAcceptance && record.vendor_trip_status === 'accepted' ? 'light-green-row' : '';
      }}
    />
  );

  const renderAssignVehicleModal = () => {
    return (
      <AssignVehicleModal
        isVisible={isAssignVehicleModalVisible}
        handleClose={(onSuccess) => {
          setState({ isAssignVehicleModalVisible: false });
          setState({ editVehicleData: {} });
          if (onSuccess) {
            fetchViewDetailsAndCount();
          }
        }}
        vehicleData={editVehicleData}
        vendorConfig={vendorConfig}
        allowCrossVendorAssignment={props.allowCrossVendorAssignment}
        phoneRegex={props.phoneRegex}
        tripType='ist'
      />
    );
  };

  const resetPasswordModal = () => {
    return (
      <Modal
        width='600px'
        visible
        onCancel={() => history.push(vehicleRequestsRoute)}
        onOk={null}
        footer={null}
        title={t("Change Password")}
      >
        <ResetPassword />
      </Modal>
    );
  };

  const renderRejectVehicleModal = () => {
    return (
      <RejectVehicleRequestModal
        isVisible={isRejectVehicleRequestModalVisible}
        rejectionReasonList={vendorConfig?.indent_settings?.rejection_reason_list || []}
        handleClose={(onSuccess) => {
          setState({ isRejectVehicleRequestModalVisible: false });
          setState({ editVehicleData: {} });
          if (onSuccess) {
            fetchViewDetailsAndCount();
          }
        }}
        vehicleData={editVehicleData}
        tripType="ist"
      />
    );
  };

  return (
    <div className={classes.mainDiv}>
      {renderNavbar()}
      <div className={classes.tabsContainer}>
        <Tabs type="card" onTabClick={onBucketChange}>
          {Object.keys(bucketList).map((key) => (
            <TabPane className={classes.tabPane} tab={renderTabHeader(key)} key={key}>
              {renderFilters()}
              {renderViewTable(key)}
            </TabPane>
          ))}
        </Tabs>
      </div>
      {isAssignVehicleModalVisible && renderAssignVehicleModal()}
      {isRejectVehicleRequestModalVisible && renderRejectVehicleModal()}
      {showPasswordResetModal && resetPasswordModal()}
      {routeInfoDetails &&
        <RouteDetailsModal
          isVisible={!!routeInfoDetails}
          routeDetails={routeInfoDetails}
          onModalClose={() => {
            setState({
              routeInfoDetails: null,
            });
          }}
        />}
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    vehicle_requests_column_list_by_bucket_ist,
    vehicle_requests_column_list_ist,
    vehicle_requests_filter_list_by_bucket_ist,
    vendor_config,
    allow_cross_vendor_vehicle_worker_assignment,
    phone_regex,
  } = state.masterData;
  return {
    bucketList: vehicle_requests_column_list_by_bucket_ist,
    columnList: vehicle_requests_column_list_ist,
    filterList: vehicle_requests_filter_list_by_bucket_ist,
    vendorConfig: vendor_config,
    allowCrossVendorAssignment: allow_cross_vendor_vehicle_worker_assignment,
    phoneRegex: phone_regex,
    vehicleRequest: state.vehicleRequest,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setFilters: (data) =>
      dispatch({
        type: SET_VEHICLE_REQUEST_FILTERS,
        data,
      }),
    setPagination: (data) =>
      dispatch({
        type: SET_VEHICLE_REQUEST_PAGINATION,
        data,
      }),
    getVehicleRequest: (data) => dispatch(getVehicleRequestIST(data)),
    getVehicleRequestCount: (data) => dispatch(getVehicleRequestCountIST(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: VehicleRequestStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(VehicleRequest);
